import { createClient } from '@/lib/supabase/client';

export type EmailType =
  | 'signup_confirmation' |'subscription_activation' |'password_reset' |'subscription_change' | 'payment_receipt' | 'watchlist_alert' | 'filter_match';

interface SendEmailOptions {
  type: EmailType;
  to: string;
  params: Record<string, string | number>;
}

/**
 * Sends a transactional email via the Supabase Edge Function (send-email),
 * which in turn calls the Resend API.
 */
export async function sendTransactionalEmail(options: SendEmailOptions): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.functions.invoke('send-email', {
    body: options,
  });
  if (error) {
    console.error('[sendTransactionalEmail] Failed to send email:', error.message);
    // Non-blocking — do not throw so auth flows are not interrupted
  }
}

/**
 * Sends a signup confirmation email.
 */
export async function sendSignupConfirmationEmail(params: {
  to: string;
  fullName: string;
  confirmationUrl: string;
}): Promise<void> {
  await sendTransactionalEmail({
    type: 'signup_confirmation',
    to: params.to,
    params: {
      fullName: params.fullName,
      confirmationUrl: params.confirmationUrl,
    },
  });
}

/**
 * Sends a subscription activation email after successful signup/payment.
 */
export async function sendSubscriptionActivationEmail(params: {
  to: string;
  fullName: string;
  plan: string;
  amountInr: number;
  expiresAt: string;
}): Promise<void> {
  await sendTransactionalEmail({
    type: 'subscription_activation',
    to: params.to,
    params: {
      fullName: params.fullName,
      plan: params.plan,
      amountInr: params.amountInr,
      expiresAt: params.expiresAt,
    },
  });
}

/**
 * Sends a password reset email.
 */
export async function sendPasswordResetEmail(params: {
  to: string;
  fullName: string;
  resetUrl: string;
}): Promise<void> {
  await sendTransactionalEmail({
    type: 'password_reset',
    to: params.to,
    params: {
      fullName: params.fullName,
      resetUrl: params.resetUrl,
    },
  });
}

/**
 * Sends a subscription change notification email.
 */
export async function sendSubscriptionChangeEmail(params: {
  to: string;
  fullName: string;
  changeType: 'upgrade' | 'downgrade' | 'cancelled' | 'renewed';
  newPlan?: string;
  expiresAt?: string;
}): Promise<void> {
  await sendTransactionalEmail({
    type: 'subscription_change',
    to: params.to,
    params: {
      fullName: params.fullName,
      changeType: params.changeType,
      ...(params.newPlan && { newPlan: params.newPlan }),
      ...(params.expiresAt && { expiresAt: params.expiresAt }),
    },
  });
}

/**
 * Sends a payment receipt email after successful Razorpay payment.
 */
export async function sendPaymentReceiptEmail(params: {
  to: string;
  fullName: string;
  orderId: string;
  paymentId: string;
  plan: 'monthly' | 'yearly';
  amountInr: number;
  renewalDate: string;
}): Promise<void> {
  await sendTransactionalEmail({
    type: 'payment_receipt',
    to: params.to,
    params: {
      fullName: params.fullName,
      orderId: params.orderId,
      paymentId: params.paymentId,
      plan: params.plan,
      amountInr: params.amountInr,
      renewalDate: params.renewalDate,
    },
  });
}

export async function sendWatchlistAlertEmail(params: {
  to: string;
  fullName: string;
  projectName: string;
  developerName: string;
  changeType: 'price' | 'inventory';
  oldValue: string;
  newValue: string;
  projectSlug: string;
}): Promise<void> {
  await sendTransactionalEmail({
    type: 'watchlist_alert',
    to: params.to,
    params: {
      fullName: params.fullName,
      projectName: params.projectName,
      developerName: params.developerName,
      changeType: params.changeType,
      oldValue: params.oldValue,
      newValue: params.newValue,
      projectSlug: params.projectSlug,
    },
  });
}

export async function sendFilterMatchEmail(params: {
  to: string;
  fullName: string;
  filterName: string;
  matchCount: number;
  projects: Array<{
    name: string;
    developer: string;
    market: string;
    price: number;
    absorption: number;
    slug: string;
  }>;
}): Promise<void> {
  await sendTransactionalEmail({
    type: 'filter_match',
    to: params.to,
    params: {
      fullName: params.fullName,
      filterName: params.filterName,
      matchCount: params.matchCount,
      projects: JSON.stringify(params.projects),
    },
  });
}
